import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionOverline } from "./Shared";

const STEPS = [
  {
    key: "assess",
    title: "Assess the State",
    body:
      "Baseline the physiology driving behavior — HRV, autonomic tone, sleep architecture, stress load, and movement capacity. Data becomes the first input into the loop.",
  },
  {
    key: "stimulus",
    title: "Prescribe the Stimulus",
    body:
      "Deliver precisely dosed exercise physiology, movement, and environmental inputs designed to shift — not exhaust — the nervous system.",
  },
  {
    key: "signal",
    title: "Reinforce the Signal",
    body:
      "Repetition, accountability, and behavioral conditioning ensure each adaptive signal lands consistently and compounds into a new pattern.",
  },
  {
    key: "adapt",
    title: "Retrain the Default",
    body:
      "The physiological set-point shifts. New behavior emerges as an output of a new state — not as an act of willpower. The loop reassesses and adapts.",
  },
];

const Framework = () => {
  const [active, setActive] = useState(0);

  return (
    <section
      id="framework"
      data-testid="framework-section"
      className="relative py-32 sm:py-40 border-t forr-canvas"
      style={{ borderColor: "var(--forr-line-soft)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <SectionOverline
                number="06"
                label="The Adaptive Physiology Framework"
                testId="framework-overline"
              />
            </Reveal>
            <Reveal delay={0.05}>
              <h2
                data-testid="framework-heading"
                className="mt-8 font-display forr-text-ink text-4xl sm:text-5xl md:text-6xl font-medium tracking-[-0.02em] leading-[1.05]"
              >
                A four-movement,{" "}
                <span className="forr-text-teal-deep">closed-loop methodology.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-md text-lg font-light forr-text-soft leading-relaxed">
                The proprietary protocol at the core of Adaptive Physiology™. Each
                movement produces measurable signal; each signal informs the next
                prescription. The loop closes — and the physiology adapts.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <div
                data-testid="framework-loop-indicator"
                className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] forr-text-teal-deep"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ backgroundColor: "var(--forr-teal)" }}
                />
                Assess → Prescribe → Reinforce → Retrain → Reassess
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-7 lg:pl-8">
            <div className="space-y-3">
              {STEPS.map((s, i) => {
                const open = i === active;
                return (
                  <Reveal delay={i * 0.06} key={s.key}>
                    <button
                      data-testid={`framework-step-${s.key}`}
                      onClick={() => setActive(i)}
                      className="w-full text-left rounded-[24px] p-8 border transition-all duration-500"
                      style={
                        open
                          ? {
                              backgroundColor: "var(--forr-teal-tint)",
                              borderColor: "var(--forr-teal)",
                              boxShadow: "var(--forr-shadow-md)",
                            }
                          : {
                              backgroundColor: "var(--forr-surface)",
                              borderColor: "var(--forr-line)",
                            }
                      }
                    >
                      <div className="flex items-start gap-6">
                        <div
                          className={`shrink-0 font-display text-2xl font-medium transition-colors ${
                            open ? "forr-text-teal-deep" : "forr-text-faint"
                          }`}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3
                              className={`font-display text-xl sm:text-2xl font-medium tracking-tight transition-colors ${
                                open ? "forr-text-ink" : "forr-text-soft"
                              }`}
                            >
                              {s.title}
                            </h3>
                            <span
                              className={`text-[11px] uppercase tracking-[0.28em] transition-colors ${
                                open ? "forr-text-teal-deep" : "forr-text-faint"
                              }`}
                            >
                              {open ? "Active" : "View"}
                            </span>
                          </div>
                          <AnimatePresence initial={false}>
                            {open && (
                              <motion.p
                                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                animate={{
                                  opacity: 1,
                                  height: "auto",
                                  marginTop: 20,
                                }}
                                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                className="forr-text-soft font-light leading-relaxed overflow-hidden"
                              >
                                {s.body}
                              </motion.p>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </button>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Framework;
